import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BrandMark } from '@/shared/components/Layout/BrandMark/BrandMark';
import { LocaleToggle } from '@/shared/components/Layout/LocaleToggle/LocaleToggle';
import { ThemeToggle } from '@/shared/components/Layout/ThemeToggle/ThemeToggle';
import { UserMenu } from '@/shared/components/Layout/UserMenu/UserMenu';
import { Button } from '@/shared/components/ui/Button/Button';
import { useBrand } from '@/shared/hooks/useBrand/useBrand';

export interface TopbarProps {
  /** The drawer this header's trigger controls. */
  mobileNavId: string;
  isMobileNavOpen: boolean;
  onOpenMobileNav: () => void;
}

/**
 * The shell's header strip.
 *
 * Deliberately holds no page title: the page owns its `h1` through `PageHeader`,
 * so the heading outline reads as one page per view instead of the brand name
 * repeated on every screen. What it does hold is the brand — mark and name from
 * `brand.*` (P28, U16), the neutral product title until a deployment sets one.
 *
 * The controls sit at the inline END, which puts them on the left in Arabic with
 * no conditional — `ms-auto` rather than `ml-auto`. The drawer trigger sits at
 * the inline START, where the navigation it opens lives, and is `lg:hidden` — the
 * exact complement of the rail's `hidden lg:flex`, so at no width are both the
 * trigger and the rail on screen.
 */
export function Topbar({ mobileNavId, isMobileNavOpen, onOpenMobileNav }: TopbarProps) {
  const { t } = useTranslation('common');
  const { t: tKit } = useTranslation('uiKit');
  const { name } = useBrand();

  return (
    <header className="flex h-14 shrink-0 items-center gap-sm border-b border-border bg-card px-lg">
      <Button
        variant="ghost"
        size="icon-sm"
        className="lg:hidden"
        aria-label={tKit('shell.openMenu')}
        aria-controls={mobileNavId}
        aria-expanded={isMobileNavOpen}
        onClick={onOpenMobileNav}
      >
        <Menu aria-hidden className="h-4 w-4" />
      </Button>

      <BrandMark size="sm" />
      <span className="truncate text-label font-medium text-foreground">
        {name === '' ? t('shell.title') : name}
      </span>
      <div className="ms-auto flex items-center gap-xs">
        <LocaleToggle />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
